import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const CONTACT_EMAIL = "trips.7starlimo@gmail.com";
const CONTACT_PHONE = "979-337-9282";
const COMPANY = "7 Star Limo Services";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "July 2026",
    sections: [
      {
        h: "1. Overview",
        p: `${COMPANY} ("we", "us", "our") respects your privacy. This policy explains what personal information we collect when you use our website or book a ride, how we use it, and the choices you have.`,
      },
      {
        h: "2. Information We Collect",
        p: "When you request a quote, create an account, or book a ride, we may collect your name, email address, phone number, pickup and drop-off addresses, travel dates and times, flight numbers, payment information (processed by our payment processor), and any special instructions you share.",
      },
      {
        h: "3. How We Use Your Information",
        p: "We use your information to provide chauffeur services, confirm and manage reservations, process payments, send booking confirmations and receipts, communicate about your trip, respond to inquiries, and improve our service. We do not sell your personal information.",
      },
      {
        h: "4. Payments",
        p: "Payments are processed by Stripe. We do not store your full card number on our servers. Stripe's privacy practices are governed by their own privacy policy.",
      },
      {
        h: "5. Sharing",
        p: "We share information only with the chauffeur assigned to your ride, our payment processor (Stripe), our email provider, and when required by law. We do not sell or rent your data to advertisers.",
      },
      {
        h: "6. Cookies & Analytics",
        p: "We use cookies and a lightweight analytics tracker to understand how visitors use our site (page views, unique visitors). This data is aggregated and does not identify you personally.",
      },
      {
        h: "7. Data Retention",
        p: "We retain your account and booking history to service your reservations and for tax, accounting, and legal purposes. You can request deletion of your account at any time by emailing us.",
      },
      {
        h: "8. Your Rights",
        p: "You can update your profile, change your password, and cancel bookings from your account dashboard. To request access, correction, or deletion of your data, email us at the address below.",
      },
      {
        h: "9. Children",
        p: "Our services are intended for adults 18+. We do not knowingly collect information from children under 13.",
      },
      {
        h: "10. Changes",
        p: "We may update this policy from time to time. Material changes will be posted here with a new \"Last updated\" date.",
      },
      {
        h: "11. Contact",
        p: `Questions? Email ${CONTACT_EMAIL} or call ${CONTACT_PHONE}.`,
      },
    ],
  },

  terms: {
    title: "Terms of Service",
    updated: "July 2026",
    sections: [
      {
        h: "1. Acceptance",
        p: `By booking a ride with ${COMPANY} or using this website, you agree to these Terms of Service. If you do not agree, please do not use our services.`,
      },
      {
        h: "2. Services",
        p: "We provide luxury chauffeured ground transportation in the Greater Houston area, including airport transfers, hourly charters, and special-event transportation. All rides are subject to vehicle availability.",
      },
      {
        h: "3. Reservations",
        p: "Reservations must be made at least 2 hours in advance of the requested pickup time. For last-minute rides, please call us and we will do our best to accommodate. All reservations require a valid payment method.",
      },
      {
        h: "4. Pricing & Payment",
        p: "Fares are quoted at time of booking. Gratuity is not included; tipping your chauffeur is optional and appreciated. Additional stops, wait time beyond 15 minutes at pickup (60 minutes for airport arrivals with Meet & Greet), and out-of-zone travel may incur additional charges. Toll fees, parking, and airport fees are billed at cost.",
      },
      {
        h: "5. Hourly Charters",
        p: "Hourly charters have a 2-hour minimum. Each booked hour includes a set amount of free miles per hour. Mileage over the included allowance is billed at the vehicle's per-mile rate.",
      },
      {
        h: "6. Airport Pickups & Delays",
        p: "We monitor your flight and adjust chauffeur arrival for delays at no charge. For arrivals with Meet & Greet, your chauffeur will wait at baggage claim with a name sign. Complimentary wait time is 60 minutes past the actual landing time; additional wait may be billed at the hourly rate.",
      },
      {
        h: "7. Passenger Conduct",
        p: "Passengers must behave respectfully and comply with vehicle rules. Smoking, illegal substances, and open containers of alcohol (where prohibited) are not permitted. Damage to the vehicle (including cleaning fees for spills, vomit, or excessive soiling) will be billed to the responsible party at a minimum of $250.",
      },
      {
        h: "8. Cancellations & No-Shows",
        p: "Please see our Cancellation Policy for full details. Rides not cancelled before the cutoff, or no-shows, are charged the full fare.",
      },
      {
        h: "9. Liability",
        p: "We carry commercial insurance as required by Texas law. To the fullest extent permitted by law, our liability is limited to the amount paid for the ride at issue. We are not liable for missed flights, appointments, or events caused by traffic, weather, or other events outside our reasonable control.",
      },
      {
        h: "10. Governing Law",
        p: "These terms are governed by the laws of the State of Texas. Any disputes will be resolved in the courts of Harris County, Texas.",
      },
      {
        h: "11. Changes to Terms",
        p: "We may update these terms from time to time. Continued use of the service after changes means you accept the updated terms.",
      },
      {
        h: "12. Contact",
        p: `Questions? Email ${CONTACT_EMAIL} or call ${CONTACT_PHONE}.`,
      },
    ],
  },

  cancellation: {
    title: "Cancellation Policy",
    updated: "July 2026",
    sections: [
      {
        h: "1. Free Cancellation Window",
        p: "You may cancel any reservation free of charge up to 1 hour before the scheduled pickup time. Manage or cancel from your account dashboard.",
      },
      {
        h: "2. Late Cancellations",
        p: "Cancellations within 1 hour of pickup cannot be processed through the website. Please call us at 979-337-9282 for assistance. Late cancellations may be charged up to the full fare at management's discretion.",
      },
      {
        h: "3. No-Shows",
        p: "If the passenger does not appear at the pickup location within 15 minutes of the scheduled time (60 minutes for airport arrivals with Meet & Greet), the ride is considered a no-show and the full fare is non-refundable.",
      },
      {
        h: "4. Airport Delays",
        p: "Flight delays are monitored automatically. We will re-time your pickup at no charge. If your flight is cancelled and you no longer need the ride, please cancel from your dashboard or call us as soon as possible.",
      },
      {
        h: "5. Changes to Your Reservation",
        p: "You may edit your reservation (change addresses, time, or vehicle) up to 1 hour before pickup from your dashboard. Any fare difference is charged or refunded at the time of the change.",
      },
      {
        h: "6. Refunds",
        p: "Approved refunds are issued to the original payment method. Please allow 5-10 business days for the refund to appear on your statement.",
      },
      {
        h: "7. Contact",
        p: `Questions or a special situation? Email ${CONTACT_EMAIL} or call ${CONTACT_PHONE} — we're available 24/7.`,
      },
    ],
  },
};

export default function Legal({ doc }) {
  const data = DOCS[doc] || DOCS.privacy;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [doc]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm mb-6" data-testid="legal-back">
            <ArrowLeft size={15} /> Back to Home
          </Link>
          <p className="text-xs tracking-[0.35em] uppercase text-primary mb-3">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-foreground mb-2" data-testid="legal-title">{data.title}</h1>
          <p className="text-sm text-muted-foreground mb-10">Last updated: {data.updated}</p>

          <div className="space-y-8 text-foreground/85 leading-relaxed" data-testid="legal-body">
            {data.sections.map((s) => (
              <section key={s.h}>
                <h2 className="text-lg text-primary font-heading mb-2">{s.h}</h2>
                <p className="text-sm">{s.p}</p>
              </section>
            ))}
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 text-xs text-muted-foreground">
            <p>
              See also:{" "}
              {["privacy", "terms", "cancellation"].filter((d) => d !== doc).map((d, i, arr) => (
                <span key={d}>
                  <Link to={`/${d === "cancellation" ? "cancellation-policy" : d === "terms" ? "terms" : "privacy"}`}
                    className="text-primary hover:underline">
                    {DOCS[d].title}
                  </Link>
                  {i < arr.length - 1 ? " · " : ""}
                </span>
              ))}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
